import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, Menu, X, ChevronRight, ArrowLeft, ChevronLeft } from 'lucide-react';
import { CATEGORIES } from '../constants';

const MobileNavBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('home');
    const [showMenu, setShowMenu] = useState(false);
    const [showCategories, setShowCategories] = useState(false);

    useEffect(() => {
        if (location.pathname === '/') setActiveTab('home');
        else setActiveTab('');
        setShowMenu(false);
        setShowCategories(false);
    }, [location]);

    const handleHomeClick = () => {
        navigate('/');
        setActiveTab('home');
        setShowMenu(false);
        setShowCategories(false);
    };

    const toggleCategories = () => {
        setShowCategories(!showCategories);
        setShowMenu(false);
        setActiveTab(showCategories ? '' : 'categories');
    };

    const toggleMenu = () => {
        setShowMenu(!showMenu);
        setShowCategories(false);
        setActiveTab(showMenu ? '' : 'menu');
    };

    const handleCategoryClick = (catId) => {
        navigate(`/category/${encodeURIComponent(catId)}`);
        setShowCategories(false);
    };
    const handleBackClick = () => {
        navigate(-1);
    };
    const menuItems = [
        { name: 'Blog', path: '/blog' },
        { name: 'About Us', path: '/about' },
        { name: 'Contact Us', path: '/contact' },
        { name: 'Privacy Policy', path: '/privacy' },
        { name: 'Terms & Conditions', path: '/terms' },
    ];

    return (
        <>
            {/* Overlays */}
            {(showCategories || showMenu) && (
                <div className="fixed inset-0 bg-black/50 z-[55] lg:hidden" onClick={() => { setShowCategories(false); setShowMenu(false); }} />
            )}

            {/* Categories Drawer */}
            <div className={`fixed bottom-16 left-0 right-0 bg-white rounded-t-2xl z-[60] transition-transform duration-300 transform ${showCategories ? 'translate-y-0' : 'translate-y-[120%]'} lg:hidden max-h-[70vh] overflow-y-auto border-t border-slate-100 shadow-2xl`}>
                <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg text-slate-800">Categories</h3>
                        <button onClick={() => setShowCategories(false)} className="p-1 bg-slate-100 rounded-full"><X className="w-5 h-5 text-slate-500" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => handleCategoryClick(cat.id)}
                                className="flex flex-col items-center justify-center p-4 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors gap-2"
                            >
                                <span className="text-2xl">{cat.icon}</span>
                                <span className="text-xs font-bold text-slate-600 text-center">{cat.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Menu Drawer */}
            <div className={`fixed bottom-16 left-0 right-0 bg-white rounded-t-2xl z-[60] transition-transform duration-300 transform ${showMenu ? 'translate-y-0' : 'translate-y-[120%]'} lg:hidden border-t border-slate-100 shadow-2xl`}>
                <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg text-slate-800">Menu</h3>
                        <button onClick={() => setShowMenu(false)} className="p-1 bg-slate-100 rounded-full"><X className="w-5 h-5 text-slate-500" /></button>
                    </div>
                    <div className="space-y-2">
                        {menuItems.map((item) => (
                            <button
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors text-left"
                            >
                                <span className="font-bold text-slate-700">{item.name}</span>
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bottom Nav Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 h-16 z-[60] lg:hidden flex items-center justify-around px-2 pb-1">

                <button
                    onClick={handleBackClick}
                    className={`flex flex-col items-center justify-center w-16 h-full gap-1 ${activeTab === 'home' ? 'text-slate-400' : 'text-slate-400'}`}
                >
                    <ChevronLeft className={`w-6 h-6 ${activeTab === 'home' ? 'text-slate-400' : ''}`} />
                    <span className="text-[10px] font-bold">Back</span>
                </button>
                <button
                    onClick={handleHomeClick}
                    className={`flex flex-col items-center justify-center w-16 h-full gap-1 ${activeTab === 'home' ? 'text-[#4048c5]' : 'text-slate-400'}`}
                >
                    <Home className={`w-6 h-6 ${activeTab === 'home' ? 'fill-current' : ''}`} />
                    <span className="text-[10px] font-bold">Home</span>
                </button>
                <button
                    onClick={toggleCategories}
                    className={`flex flex-col items-center justify-center w-16 h-full gap-1 ${showCategories ? 'text-[#4048c5]' : 'text-slate-400'}`}
                >
                    <LayoutGrid className={`w-6 h-6 ${showCategories ? 'fill-current' : ''}`} />
                    <span className="text-[10px] font-bold">Categories</span>
                </button>

                <button
                    onClick={toggleMenu}
                    className={`flex flex-col items-center justify-center w-16 h-full gap-1 ${showMenu ? 'text-[#4048c5]' : 'text-slate-400'}`}
                >
                    <Menu className="w-6 h-6" />
                    <span className="text-[10px] font-bold">Menu</span>
                </button>
            </div>
        </>
    );
};

export default MobileNavBar